// Lazily-fetched checkout-sessions list for a single payment, shared by the
// Payments-tab advanced table's actions cell, its Status/"Link expires"/
// "Delivery" columns (only fetched eagerly for link payments), and its
// detail-panel timeline (fetched on row expand). Extracted out of the old
// PaymentCard.jsx so every consumer of a payment's sessions shares one cache
// instead of each re-fetching independently.
import { useCallback, useEffect, useState } from 'react';
import { callApi } from 'src/utils/apiCall';

// Deliberately NOT Redux — many cells/panels can be mounted at once for
// different payments, and a single global slice would have them clobber
// each other's data. A per-payment Map keyed by practice/lead/treatment/
// payment is enough, and outlives any one cell's mount/unmount.
export const sessionsCache = new Map();
export const cacheKey = (practiceId, leadId, treatmentId, paymentId) => `${practiceId}:${leadId}:${treatmentId}:${paymentId}`;

// The Status, "Link expires", and "Delivery" cells for the SAME row all
// eagerly fetch a link payment's sessions independently and concurrently —
// without de-duping, that's 3 simultaneous requests for identical data, and
// whichever resolves LAST silently wins the cache regardless of the other
// two's (possibly-mocked-differently-in-tests, but always logically
// identical) responses. `pendingFetches` collapses concurrent callers for
// the same key onto one in-flight request; `subscribers` then broadcasts
// that single result to every mounted hook instance watching the key, so a
// cell that didn't happen to own the fetch still re-renders with the result.
const pendingFetches = new Map();
const subscribers = new Map();

function notify(key, results) {
    const subs = subscribers.get(key);
    if (subs) subs.forEach((fn) => fn(results));
}

function subscribe(key, fn) {
    if (!subscribers.has(key)) subscribers.set(key, new Set());
    subscribers.get(key).add(fn);
    return () => {
        const subs = subscribers.get(key);
        if (!subs) return;
        subs.delete(fn);
        if (subs.size === 0) subscribers.delete(key);
    };
}

async function fetchSessionsForKey(key, url) {
    if (pendingFetches.has(key)) return pendingFetches.get(key);
    const promise = (async () => {
        let results;
        try {
            const res = await callApi(url);
            results = res?.ok && Array.isArray(res.data?.results) ? res.data.results : [];
        } catch {
            results = [];
        }
        sessionsCache.set(key, results);
        notify(key, results);
        return results;
    })();
    pendingFetches.set(key, promise);
    try {
        return await promise;
    } finally {
        pendingFetches.delete(key);
    }
}

/**
 * @param {object} payment
 * @param {object} params
 * @param {*} params.practiceId
 * @param {*} params.leadId
 * @param {*} params.treatmentId
 * @param {boolean} [params.eager] - fetch immediately on mount (link-payment
 *   rows need sessions for their Status/"Link expires"/"Delivery" cells even
 *   before the row is expanded; other rows only fetch on demand).
 */
export default function usePaymentSessions(payment, { practiceId, leadId, treatmentId, eager = false } = {}) {
    const key = cacheKey(practiceId, leadId, treatmentId, payment?.id);
    const [sessions, setSessions] = useState(() => sessionsCache.get(key) || null);
    const [sessionsLoading, setSessionsLoading] = useState(false);

    // Subscribed BEFORE any fetch this instance triggers below, so a fetch
    // "owned" by a sibling cell for the same key still reaches this one.
    useEffect(() => {
        if (sessionsCache.has(key)) setSessions(sessionsCache.get(key));
        return subscribe(key, setSessions);
    }, [key]);

    const fetchSessions = useCallback(
        async (force) => {
            if (!force && sessionsCache.has(key)) {
                setSessions(sessionsCache.get(key));
                return;
            }
            if (!practiceId || !leadId || !treatmentId || !payment?.id) return;
            if (force) sessionsCache.delete(key);
            setSessionsLoading(true);
            try {
                await fetchSessionsForKey(
                    key,
                    `/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/payments/${payment.id}/checkout_sessions/`
                );
            } finally {
                setSessionsLoading(false);
            }
        },
        [key, practiceId, leadId, treatmentId, payment?.id]
    );

    useEffect(() => {
        if (eager && !sessionsCache.has(key)) fetchSessions(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eager, key]);

    const latestSession = Array.isArray(sessions) && sessions.length > 0
        ? sessions.find((s) => String(s.id) === String(payment?.checkout_summary?.latest_checkout_session_id)) || sessions[0]
        : null;

    const refresh = useCallback(() => {
        sessionsCache.delete(key);
        fetchSessions(true);
    }, [key, fetchSessions]);

    return { sessions, sessionsLoading, fetchSessions, latestSession, refresh };
}
